import React, {createContext, useContext, useEffect, useState} from 'react';
import {Navigate} from 'react-router';
import type {Role} from '@ait-tracker/shared';
import {api, ssoLoginUrl, type SessionUser} from '../lib/api';
import {MOCK_ENABLED} from '../dev/mock';
import {FullScreenLoadingState} from '../components/States';

interface AuthContextValue {
  /** The signed-in user — also who the API authorizes as (the session cookie IS this user). */
  user: SessionUser | null;
  isAuthenticated: boolean;
  /**
   * False until the initial session probe completes (SSO redirect landing / refresh). Route guards
   * wait on this so a cookie-backed session isn't bounced to login before it's hydrated.
   */
  authReady: boolean;
  /** Begin Entra SSO — full-page redirect to the backend (spec 005). The only sign-in path. */
  loginWithMicrosoft: () => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const SESSION_KEY = 'ait_user';

const read = (key: string): SessionUser | null => {
  const raw = sessionStorage.getItem(key);
  return raw ? (JSON.parse(raw) as SessionUser) : null;
};

export function AuthProvider({children}: {children: React.ReactNode}): React.ReactElement {
  // The HttpOnly ait_session cookie is the real credential; `sessionUser` mirrors it for the React
  // app (and survives refresh via sessionStorage).
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(() => read(SESSION_KEY));
  // Ready immediately if we already have a cached user or we're in mock mode; otherwise probe the
  // cookie-backed session once (this is how the SSO redirect landing hydrates identity).
  const [authReady, setAuthReady] = useState<boolean>(() => MOCK_ENABLED || read(SESSION_KEY) !== null);

  const persist = (session: SessionUser | null) => {
    if (session) sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(SESSION_KEY);
    setSessionUser(session);
  };

  // One-shot session probe for the SSO redirect landing (and refresh): if we hold no cached user,
  // ask the server who we are via the HttpOnly cookie. 401 → stay logged out. Skipped in mock mode.
  useEffect(() => {
    if (authReady) return;
    let cancelled = false;
    api
      .getMe()
      .then(({user}) => {
        if (!cancelled) persist(user);
      })
      .catch(() => {
        /* no/invalid session — remain logged out */
      })
      .finally(() => {
        if (!cancelled) setAuthReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loginWithMicrosoft = () => {
    window.location.href = ssoLoginUrl('/dashboard');
  };

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      persist(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user: sessionUser,
        isAuthenticated: sessionUser !== null,
        authReady,
        loginWithMicrosoft,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function RequireAuth({children}: {children: React.ReactNode}): React.ReactElement {
  const {isAuthenticated, authReady} = useAuth();
  // Wait for the initial session probe (SSO landing) before deciding — avoids bouncing a valid
  // cookie-backed session to login on first paint.
  if (!authReady) return <FullScreenLoadingState label="Signing you in…" />;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Gate a route to users holding at least one of `roles`. Server-side authz is the real
 * enforcement (FR-006); this only keeps the UI honest by not routing unauthorized users to a
 * console they can't use. Authenticated-but-unauthorized users are sent to their dashboard.
 */
export function RequireRole({roles, children}: {roles: Role[]; children: React.ReactNode}): React.ReactElement {
  const {user, isAuthenticated, authReady} = useAuth();
  if (!authReady) return <FullScreenLoadingState label="Signing you in…" />;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (!user || !user.roles.some(r => roles.includes(r))) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}
